import { UpdatePurpose } from '../components/financialTables/configuration/constants';
import { RECONCILIATIONS_ALIASES } from '../constnats/schemaConstants';
import { UPDATE_PHASE } from '../constnats/tableUpdate';
import type { TableUpdateInfoType } from '../schemas/tableUpdate.schemas';

// Phases that count towards an employee's active review workload.
export const REVIEW_PHASES = [UPDATE_PHASE.READY_FOR_PEER_REVIEW, UPDATE_PHASE.READY_FOR_QA_REVIEW];
const REVIEW_PHASE_SET = new Set<string>(REVIEW_PHASES);

export const DEFAULT_MAX_ACTIVE_REVIEW_UPDATES = 5;

// Per-employee allowance overrides
export const PROMOTION_ALLOWANCE_OVERRIDES: Record<number, number> = {
    2538926: 5,
    978158: 5,
    1253901: 5,
    1249299: 5,
    997797: 999,
    1191457: 10,
    2468800: 10,
    2491: 10,
};

export function getPromotionAllowanceLimit(userId: number): number {
    return PROMOTION_ALLOWANCE_OVERRIDES[userId] ?? DEFAULT_MAX_ACTIVE_REVIEW_UPDATES;
}

// Service / non-working accounts that should never be subject to (or listed by) the
// promotion allowance rule.
export const BLACKLISTED_EMPLOYEE_NAMES = new Set([
    'Vilizar Todorov',
    'System User',
    'Stefan Tanev',
    'Stef Testing',
    'Jörg Geyer',
    'Beth D.',
    'Administrator',
]);

// Whether an account is exempt from the promotion allowance rule (blacklisted service
// account or a tagged system account).
export function isAllowanceExempt(fullName: string): boolean {
    return BLACKLISTED_EMPLOYEE_NAMES.has(fullName) || fullName.includes('[SYSTEM]');
}

// Update summaries are built as `${companyName} (${schemaName})` when the draft is
// created and are never edited afterwards, so the trailing parenthesised segment is the
// schema the update belongs to. Schemas themselves carry no type discriminator — a
// reconciliation is identified by its name alone.
const SUMMARY_SCHEMA_NAME = /\(([^()]*)\)\s*$/;

// Whether an update requires and consumes promotion allowance. Deferred review execution
// is exempt, and an unparseable summary never blocks promotion.
export function isPromotionAllowanceApplicable(
    update: Pick<TableUpdateInfoType, 'summary' | 'purpose'>
): boolean {
    if (update.purpose === UpdatePurpose.DEFERRED_REVIEWS_EXECUTION) {
        return false;
    }
    const schemaName = update.summary.match(SUMMARY_SCHEMA_NAME)?.[1];
    if (!schemaName) {
        return false;
    }
    return RECONCILIATIONS_ALIASES.some((alias) => schemaName.includes(alias));
}

// Counts, per owner, reconciliation updates subject to allowance in a review phase.
export function countActiveReconciliationsByOwner(
    updates: TableUpdateInfoType[]
): Map<number, number> {
    const countByOwner = new Map<number, number>();
    const countedUpdateIds = new Set<number>();
    for (const update of updates) {
        // The unpaged endpoint left-joins the assignees collection without DISTINCT, so
        // it may return the same update once per assignee. Count every update only once.
        if (countedUpdateIds.has(update.id)) {
            continue;
        }
        countedUpdateIds.add(update.id);
        if (!REVIEW_PHASE_SET.has(update.phase) || !isPromotionAllowanceApplicable(update)) {
            continue;
        }
        countByOwner.set(update.ownerId, (countByOwner.get(update.ownerId) ?? 0) + 1);
    }
    return countByOwner;
}
